import React from 'react';
import type {ExecutorNotification} from '@rocket-ui/lk-api';
import {Link} from 'react-router-dom';
import {CloseIcon} from 'shared/icons';
import {Drawer} from 'shared/ui/Drawer';
import styles from './NotificationDetailsDrawer.module.css';

const APP_ROUTE_PREFIXES = [
	'/orders',
	'/offers',
	'/wallet',
	'/notifications',
	'/profile',
	'/registration',
	'/download-app',
];

type NotificationDetailsDrawerProps = {
	notification: ExecutorNotification | null;
	onClose: () => void;
};

function formatNotificationDateTime(value: string): string {
	const date = new Date(value);

	if (Number.isNaN(date.getTime())) return '';

	const day = new Intl.DateTimeFormat('ru-RU', {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
	})
		.format(date)
		.replace(/\s*г\.?$/, '');
	const time = new Intl.DateTimeFormat('ru-RU', {
		hour: '2-digit',
		minute: '2-digit',
	}).format(date);

	return `${day} в ${time}`;
}

function actionDeeplink(action?: ExecutorNotification['action']): string | undefined {
	if (!action) return undefined;

	const raw = action.deepLink ?? (action as {deeplink?: string}).deeplink;
	const value = raw?.trim();

	return value || undefined;
}

function internalPath(href: string): string | null {
	if (href.startsWith('/') && !href.startsWith('//')) return href;

	try {
		const url = new URL(href);
		const path = `${url.pathname}${url.search}${url.hash}`;
		const isAppPath = APP_ROUTE_PREFIXES.some(
			(prefix) => url.pathname === prefix || url.pathname.startsWith(`${prefix}/`),
		);
		if (isAppPath || (typeof window !== 'undefined' && url.origin === window.location.origin)) return path;
	} catch {
		return null;
	}

	return null;
}

function notificationDetails(notification: ExecutorNotification): {
	subtitle?: string;
	title: string;
	text?: string;
	href?: string;
	actionText?: string;
	deeplink?: string;
} {
	const sourceTitle = notification.default?.title?.trim();
	const payload = notification.action ?? notification.default;
	const messageTitle = payload?.title?.trim();
	const subtitle =
		payload?.subtitle?.trim() ||
		(payload !== notification.default ? notification.default?.subtitle?.trim() : undefined) ||
		(sourceTitle && messageTitle && sourceTitle !== messageTitle ? sourceTitle : undefined);

	return {
		subtitle: subtitle && subtitle !== messageTitle ? subtitle : undefined,
		title: messageTitle || sourceTitle || 'Уведомление',
		text: payload?.body?.trim() || undefined,
		href: notification.action?.type === 'open_link' ? notification.action.url : undefined,
		actionText: notification.action?.actionText?.trim() || undefined,
		deeplink: actionDeeplink(notification.action),
	};
}

function DeeplinkButton({
	href,
	label,
	onNavigate,
}: {
	href: string;
	label: string;
	onNavigate: () => void;
}): React.ReactElement {
	const path = internalPath(href);

	if (path) {
		return (
			<Link to={path} className={styles.action} onClick={onNavigate}>
				{label}
			</Link>
		);
	}

	return (
		<a className={styles.action} href={href} target="_blank" rel="noreferrer">
			{label}
		</a>
	);
}

function RocketMark(): React.ReactElement {
	return (
		<svg viewBox="0 0 30 20" className={styles.logo} fill="none" aria-hidden>
			<path
				d="M0 0V2.50637L15.0012 3.91585L28.2241 5.04116C28.3039 5.04684 28.3039 5.16619 28.2241 5.17188L15.0012 6.31992L0 7.75781V12.2249L15.0012 13.6628L28.2241 14.8109C28.3039 14.8166 28.3039 14.9359 28.2241 14.9416L15.0012 16.0669L0 17.4764V19.9828L30.0023 17.1127V12.6342L15.0012 11.2019L1.70986 10.0482C1.63006 10.0425 1.63006 9.92318 1.70986 9.9175L15.0012 8.76377L30.0023 7.33156V2.85306L0 0Z"
				fill="currentColor"
			/>
		</svg>
	);
}

export function NotificationDetailsDrawer({notification, onClose}: NotificationDetailsDrawerProps): React.ReactElement {
	const [shown, setShown] = React.useState(notification);

	if (notification != null && notification !== shown) {
		setShown(notification);
	}

	const content = shown ? notificationDetails(shown) : null;

	return (
		<Drawer
			open={notification != null}
			onClose={onClose}
			contentBackground="white"
			bodyClassName={styles.body}
			panelClassName={styles.panel}
		>
			{shown && content ? (
				<>
					<div className={styles.header}>
						<div className={styles.top}>
							<button type="button" className={styles.close} onClick={onClose} aria-label="Закрыть">
								<CloseIcon width={24} height={24} />
							</button>
							<RocketMark />
						</div>
						{shown.createdAt ? <p className={styles.date}>{formatNotificationDateTime(shown.createdAt)}</p> : null}
						{content.subtitle ? <p className={styles.subtitle}>{content.subtitle}</p> : null}
					</div>
					<div className={styles.content}>
						<h2 className={styles.title}>{content.title}</h2>
						{content.text ? <p className={styles.text}>{content.text}</p> : null}
						{content.deeplink ? (
							<DeeplinkButton
								href={content.deeplink}
								label={content.actionText || 'Перейти'}
								onNavigate={onClose}
							/>
						) : null}
						{content.href && !content.deeplink ? (
							<a className={styles.link} href={content.href} target="_blank" rel="noreferrer">
								{content.actionText || 'Подробнее'}
							</a>
						) : null}
					</div>
				</>
			) : null}
		</Drawer>
	);
}
